import { SupportAgent } from '@mui/icons-material';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import DeleteIcon from '@mui/icons-material/Delete';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { PageHeader } from 'components/ui/Global/PageHeader';
import { SingleProductView } from 'components/ui/SingleProductView';
import dayjs from 'dayjs';
import { convertToTicketUpdate } from 'functions/Converters';
import useWindowsDimension from 'functions/useWindowsDimension';
import { rqPost } from 'http/apiRoutes';
import { usePut } from 'http/useApi';
import { IProductViewModel } from 'Interfaces/IProductConfig';
import {
  ITicketBillingUpdateViewModel,
  ITicketBillingViewModel,
  ITicketPostViewModel,
} from 'Interfaces/ITicketConfig';
import { useEffect, useReducer } from 'react';
import { EditBillingReducer, EditBillingState } from './EditBillingReducer';

export type EditTicketBillingFormProps = {
  ticket: ITicketBillingViewModel;
  onSave: (editedTicket: ITicketBillingViewModel) => void;
  onCancel: () => void;
  totalBillingValue?: number;
};

type EditTicketBillingFormValues = {
  ticket: ITicketBillingViewModel;
};

export const EditTicketBillingForm = ({
  ticket,
  onSave,
  onCancel,
  totalBillingValue,
}: EditTicketBillingFormProps) => {
  const [state, dispatch] = useReducer(EditBillingReducer, {
    ...EditBillingState,
    editedTicket: {
      ...ticket,
      ticketPosts: ticket.ticketPosts.map((post) => ({ ...post })),
    },
  });
  const { width, height } = useWindowsDimension();
  const putBilling = usePut(rqPost.billingSupport(ticket.id, true),);
  const handlePostChange = (
    index: number,
    field: keyof ITicketPostViewModel,
    value: any
  ) => {
    dispatch({
      type: 'UPDATE_TICKET_POST',
      payload: { index, field, value },
    });
  };

  useEffect(() => {
    console.log('State updated:', state);

    // Calculate total from billable posts only
    const postsTotal = state.editedTicket.ticketPosts.reduce(
      (sum: number, post: ITicketPostViewModel) =>
        post.isBillable ? sum + post.totalBillingValue : sum,
      0
    );

    let totalBillingValue = 0;

    if (state.editedTicket.fixedPrice >= 0) {
      totalBillingValue = state.editedTicket.fixedPrice;
    } else if (state.editedTicket.maxPrice >= 0) {
      totalBillingValue = Math.min(state.editedTicket.maxPrice, postsTotal);
    } else {
      totalBillingValue = postsTotal;
    }
    // add consumables
    totalBillingValue += state.editedTicket.consumableProducts.reduce(
      (sum: number, product: IProductViewModel) =>
        product.isActive ? sum + product.retailPrice : sum,
      0
    );

    console.log('State updated totalBillingValue:', totalBillingValue);
    dispatch({
      type: 'SET_TOTAL_BILLING_VALUE',
      payload: { totalBillingValue },
    });
  }, [state.editedTicket]);

  // Handle message change
  const handleMessageChange = (index: number, value: string) => {
    handlePostChange(index, 'message', value);
  };
  // Handle isBillable change
  const handleIsBillableChange = (index: number, checked: boolean) => {
    if (index < 0 || index >= state.editedTicket.ticketPosts.length) {
      console.error(
        'Invalid index:',
        index,
        'Posts length:',
        state.editedTicket.ticketPosts.length
      );
      return;
    }
    handlePostChange(index, 'isBillable', checked);
  };

  // Handle totalBillingValue change
  const handleBillingValueChange = (index: number, value: string) => {
    const numericValue = parseFloat(value) || 0;
    handlePostChange(index, 'totalBillingValue', numericValue);
  };
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select(); // Select the entire value
  };
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(state.editedTicket);

    const updatedTicket = convertToTicketUpdate(state.editedTicket);

    console.log('ticket mutation PUT', updatedTicket);

    const billingUpdateData: ITicketBillingUpdateViewModel = {
      id: state.editedTicket.id,
      title: state.editedTicket.title,
      ticketPosts: state.editedTicket.ticketPosts.map((post) => ({
        id: post.id,
        message: post.message,
        totalBillingValue: post.totalBillingValue,
        isBillable: post.isBillable,
        isChanged: post.isChanged || false,
      })),
      consumableProducts: state.editedTicket.consumableProducts.map(
        (product) => ({
          id: product.id,
          isBillable: !product.isAlreadyBilled,
          isChanged: product.isActive,
        })
      ),
    };

    console.log('billing support mutation PUT', billingUpdateData);

    putBilling.mutate(billingUpdateData, {
      onSuccess: (res: any) => {
        onSave(res);
      },
      onError: (error: any) => {
        console.error('Error updating billing:', error);
      },
    });
  };
  const handleRemoveProduct = (index: number) => {
    dispatch({
      type: 'REMOVE_CONSUMABLE_PRODUCT',
      payload: { index },
    });
  };
  const handleTitleChange = (value: string) => {
    dispatch({
      type: 'UPDATE_TICKET_FIELD',
      payload: { field: 'title', value },
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid item xs={12}>
        <PageHeader
          Icon={SupportAgent}
          title={'Edit Ticket'}
          FormJSX={null}
          HeaderButtons={[]}
        />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label='Title'
              value={state.editedTicket.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              onFocus={handleFocus}
              variant='outlined'
              size='small'
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label='Ticket Number'
              value={state.editedTicket.ticketNo}
              InputProps={{
                readOnly: true,
              }}
              variant='outlined'
              size='small'
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label='Total Billing Value'
              value={state.totalBillingValue}
              InputProps={{
                readOnly: true,
              }}
              variant='outlined'
              size='small'
            />
          </Grid>
        </Grid>
      </Grid>

      <Typography variant='subtitle1' sx={{ mt: 2, mb: 1 }}>
        Ticket Posts
      </Typography>

      {state.editedTicket.ticketPosts.map(
        (post: ITicketPostViewModel, index: number) => {
          console.log('Rendering post:', post, 'at index:', index);
          const dateTime = dayjs(post.startTime);
          const startTime = dayjs(post.startTime);
          const endTime = dayjs(post.endTime);
          const duration = endTime.diff(startTime, 'minute');

          return (
            <Box
              key={index}
              sx={{
                mb: 2,
                p: 2,
                border: '1px solid',
                borderColor: post.isChanged ? 'warning.back' : 'primary.light',
                borderRadius: 1.5,
                height: 220,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                backgroundColor: post.isChanged
                  ? 'warning.dark'
                  : 'primary.main',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              {/* Header Section */}
              <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                sx={{
                  mb: 1.5,
                  p: 1,
                  borderRadius: 1,
                  minHeight: 32,
                  flexShrink: 0,
                  border: '1px solid',
                  borderColor: 'primary.light',
                }}
              >
                <Box display='flex' alignItems='center' gap={1}>
                  <Typography
                    variant='caption'
                    sx={{
                      fontWeight: 'medium',
                      color: 'text.secondary',
                      backgroundColor: 'primary.light',
                      px: 0.8,
                      py: 0.3,
                      borderRadius: 0.8,
                      fontSize: 10,
                    }}
                  >
                    {dateTime.format('DD-MM-YYYY')}
                  </Typography>

                  {/* Conditional Icons */}
                  <Box display='flex' alignItems='center' gap={0.3}>
                    {post.isEmailSent && (
                      <IconButton
                        aria-label='Email sent'
                        size='small'
                        sx={{
                          backgroundColor: 'success.light',
                          '&:hover': { backgroundColor: 'success.main' },
                          width: 24,
                          height: 24,
                        }}
                      >
                        <MarkEmailReadRoundedIcon
                          sx={{ fontSize: 14, color: 'white' }}
                        />
                      </IconButton>
                    )}
                    {post.isCCSent && (
                      <IconButton
                        aria-label='CC sent'
                        size='small'
                        sx={{
                          backgroundColor: 'info.light',
                          '&:hover': { backgroundColor: 'info.main' },
                          width: 24,
                          height: 24,
                        }}
                      >
                        <ContactMailIcon
                          sx={{ fontSize: 14, color: 'white' }}
                        />
                      </IconButton>
                    )}
                    {post.isOvertime && (
                      <IconButton
                        aria-label='Overtime'
                        size='small'
                        sx={{
                          backgroundColor: 'warning.light',
                          '&:hover': { backgroundColor: 'warning.main' },
                          width: 24,
                          height: 24,
                        }}
                      >
                        <MoreTimeRoundedIcon
                          sx={{ fontSize: 14, color: 'white' }}
                        />
                      </IconButton>
                    )}
                    {post.isBillable && (
                      <IconButton
                        aria-label='Billable'
                        size='small'
                        sx={{
                          backgroundColor: 'success.light',
                          '&:hover': { backgroundColor: 'success.main' },
                          width: 24,
                          height: 24,
                        }}
                      >
                        <AttachMoneyRoundedIcon
                          sx={{ fontSize: 14, color: 'white' }}
                        />
                      </IconButton>
                    )}
                    {post?.attachments && post.attachments.length > 0 && (
                      <IconButton
                        aria-label='Attachments'
                        size='small'
                        sx={{
                          width: 24,
                          height: 24,
                        }}
                      >
                        <AttachFileRoundedIcon
                          sx={{ fontSize: 14, color: 'text.secondary' }}
                        />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <Typography
                  variant='caption'
                  sx={{
                    fontWeight: 'medium',
                    color: 'text.secondary',
                    px: 1,
                    py: 0.3,
                    borderRadius: 0.8,
                    fontSize: 10,
                  }}
                >
                  {dayjs(post.startTime).format('HH:mm')} -{' '}
                  {dayjs(post.endTime).format('HH:mm')}
                  <Box
                    component='span'
                    sx={{ fontWeight: 'bold', color: 'primary.main', ml: 0.3 }}
                  >
                    {duration} min
                  </Box>
                </Typography>
              </Box>

              {/* Form Content Section */}
              <Box
                sx={{
                  flexGrow: 1,
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                {/* Message TextField */}
                <TextField
                  fullWidth
                  label='Message'
                  value={post.message}
                  onChange={(e) => handleMessageChange(index, e.target.value)}
                  onFocus={handleFocus}
                  multiline
                  rows={2} // Reduced rows
                  variant='outlined'
                  size='small'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      minHeight: '64px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: 1.5,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: 13,
                      fontWeight: 'medium',
                    },
                  }}
                />

                {/* Bottom Row  */}
                <Box
                  display='flex'
                  gap={1.5}
                  alignItems='center'
                  sx={{ mt: 'auto' }}
                >
                  <TextField
                    fullWidth
                    label='Billing Value'
                    type='number'
                    value={post.totalBillingValue}
                    onChange={(e) =>
                      handleBillingValueChange(index, e.target.value)
                    }
                    onFocus={handleFocus}
                    inputProps={{ step: '0.01', min: '0' }}
                    variant='outlined'
                    size='small'
                    sx={{
                      maxWidth: 150,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        fontSize: '0.875rem',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: 13,
                        fontWeight: 'medium',
                      },
                    }}
                  />

                  <Box
                    display='flex'
                    alignItems='center'
                    gap={0.8}
                    sx={{
                      backgroundColor: post.isBillable
                        ? 'success.light'
                        : 'transparent',
                      px: 1.5,
                      py: 0.8,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: post.isBillable
                        ? 'success.main'
                        : 'grey.300',
                      minWidth: 'fit-content',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: 'medium',
                        fontSize: '0.8125rem',
                        color: post.isBillable
                          ? 'success.dark'
                          : 'text.secondary',
                      }}
                    >
                      Billable
                    </Typography>
                    <Switch
                      checked={post.isBillable}
                      onChange={(e) =>
                        handleIsBillableChange(index, e.target.checked)
                      }
                      color='success'
                      size='small' // Smaller switch
                      sx={{
                        '& .MuiSwitch-track': {
                          backgroundColor: post.isBillable
                            ? 'success.main'
                            : 'grey.400',
                          height: 16,
                          width: 28,
                        },
                        '& .MuiSwitch-thumb': {
                          width: 12,
                          height: 12,
                          backgroundColor: 'white',
                        },
                        '& .MuiSwitch-switchBase': {
                          padding: 1,
                          '&.Mui-checked': {
                            transform: 'translateX(12px)',
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        }
      )}

      {state.editedTicket.consumableProducts.map(
        (post: IProductViewModel, index: number) => {
          return (
            <Box
              key={index}
              sx={{
                transform: `scale(${0.8})`,
                transformOrigin: 'left',
                width: '100%',
                position: 'relative',
              }}
            >
              {/* Trash icon */}
              <IconButton
                onClick={() => handleRemoveProduct(index)}
                sx={{
                  position: 'absolute',
                  top: 3,
                  right: 5,
                  zIndex: 10,
                  backgroundColor: 'rgba(137, 143, 145, 0.8)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,0,0,0.2)',
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>

              <SingleProductView product={post} />
            </Box>
          );
        }
      )}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button variant='outlined' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='contained' type='submit' onClick={onSubmit}>
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};
